import React, { Dispatch, SetStateAction, useState } from "react"
import EditSidebar from "../../../../../Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "../../../../../Ui/EditSidebar/EditSidebarHeader"
import {
  Order,
  Reason,
  ShippingFee,
  ShippingFeeModificationType
} from "lib/types/generated/graphql-types"
import PrimaryButton from "../../../../../Ui/Buttons/PrimaryButton"
import { useMutation } from "@apollo/client"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { useDispatch } from "react-redux"
import { handleErrorMessages } from "helpers/errors"
import { ReactComponent as ShippingIcon } from "images/icons/cart-flatbed-boxes.svg"
import { Error, Label, Wrapper } from "./UpdateShippingFee.styled"
import Money from "../../../../../Money"
import DineroFactory from "dinero.js"
import Input from "../../../../../Ui/Form/Input"
import CREATE_SHIPPING_FEE_MODIFICATION from "../../../../../../graphql/mutations/order/modification/shippingFees/CreateModification"
import ModificationPercentage from "../../ModifyOrderLines/EditOrderLine/ModificationPercentage"

type Props = {
  order: Order
  refetch: () => void
  editShippingFee: ShippingFee | undefined
  setEditShippingFee: Dispatch<SetStateAction<ShippingFee | undefined>>
  reason: Reason
}

const AddOrUpdateShippingFeeSideBar = ({
  order,
  refetch,
  editShippingFee,
  setEditShippingFee,
  reason
}: Props) => {
  const [discount, setDiscount] = useState<number>()
  const dispatch = useDispatch()
  const [percentage, setPercentage] = useState<number>()
  const [customPercentage, setCustomPercentage] = useState<string>()

  const reset = () => {
    refetch()
    setEditShippingFee(undefined)
    setDiscount(undefined)
    dispatch(hideEditSidebar())
    window.scrollTo(0, 0)
  }

  const getCreateData = () => {
    if (editShippingFee && discount) {
      return {
        id: editShippingFee.id,
        taxGroupId: editShippingFee.taxGroupId,
        price: {
          basePriceAmount: editShippingFee.basePriceAmount,
          ...(editShippingFee?.salePriceAmount && {
            salePriceAmount: editShippingFee.salePriceAmount - discount
          }),
          taxPercentage: editShippingFee.taxPercentage
        }
      }
    }
  }

  const [createModification, { loading: createLoading }] = useMutation(
    CREATE_SHIPPING_FEE_MODIFICATION,
    {
      onCompleted: () => {
        reset()
      },
      onError: (error) => handleErrorMessages(error)
    }
  )

  const getShippingFeeInput = () => {
    return {
      modificationType: ShippingFeeModificationType.Update,
      data: JSON.stringify(getCreateData())
    }
  }

  const onCreate = () => {
    createModification({
      variables: {
        orderId: order.id,
        input: {
          ...(reason?.cause && reason?.code && {reason}),
          shippingFees: [getShippingFeeInput()]
        }
      }
    })
  }

  const cancel = () => {
    setEditShippingFee(undefined)
    dispatch(hideEditSidebar())
    setDiscount(0)
  }

  const amountToHigh = !!(
    discount &&
    editShippingFee?.salePriceAmount &&
    discount > editShippingFee?.salePriceAmount
  )

  const handleDiscountPercentage = (pct: number) => {
    const shippingFeeAmount = editShippingFee?.salePriceAmount ?? 0

    if (pct) {
      const amount = Math.floor(shippingFeeAmount * (pct / 100))
      setDiscount(amount)
    }
  }

  const handleDiscountInput = (value: number) => {
    setPercentage(undefined)
    setCustomPercentage(undefined)
    setDiscount(value)
  }

  return (
    <EditSidebar cancelEvent={cancel}>
      <EditSidebarHeader cancelEvent={cancel} title={"Edit shipping fee"}>
        <PrimaryButton
          disabled={!discount || discount < 1 || amountToHigh}
          handleClick={() => onCreate()}
          loading={createLoading}
        >
          {"Update shipping fee"}
        </PrimaryButton>
      </EditSidebarHeader>
      <>
        <Label>ShippingFee</Label>
        <Wrapper>
          <ShippingIcon />
          <div>
            <div>{editShippingFee?.name}</div>
            <div>
              <b>
                <Money
                  amount={editShippingFee?.salePriceAmount ?? 0}
                  currencyUnit={editShippingFee?.currencyCode as DineroFactory.Currency}
                />
              </b>
            </div>
          </div>
        </Wrapper>
        <ModificationPercentage
          percentage={percentage}
          setPercentage={setPercentage}
          handleDiscountPercentage={handleDiscountPercentage}
          customPercentage={customPercentage}
          setCustomPercentage={setCustomPercentage}
        />
        <Label>Discount</Label>
        <Input
          type="number"
          name="discount"
          placeholder="Amount in minor units"
          value={discount}
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            handleDiscountInput(parseInt(e.currentTarget.value))
          }
        />
        {amountToHigh ? (
          <Error>- Amount to high</Error>
        ) : (
          <>
            <strong>Example: </strong>
            {discount ? (
              <Money
                amount={parseInt(`${discount}`)}
                currencyUnit={editShippingFee?.currencyCode as DineroFactory.Currency}
              />
            ) : (
              "0"
            )}
          </>
        )}
      </>
    </EditSidebar>
  )
}

export default AddOrUpdateShippingFeeSideBar
